/**
 * Anbieterübergreifende Auswahlstufen. `none` bedeutet, dass kein zusätzlicher
 * Reasoning-Aufwand angefordert wird; unbekannte Modelle werden nie anhand
 * ihres Namens als Reasoning-Modell geraten.
 */
export const REASONING_STUFEN = [
    "none",
    "low",
    "medium",
    "high",
    "xhigh",
    "max",
];
const NONE = ["none"];
const DEEPSEEK_V4_PRO = [
    "none",
    "high",
    "max",
];
const DEEPSEEK_V4_FLASH = [
    "none",
    "high",
    "max",
];
const OPENAI_54_55 = [
    "none",
    "low",
    "medium",
    "high",
    "xhigh",
];
const OPENAI_56 = [
    ...OPENAI_54_55,
    "max",
];
const XAI_43 = [
    "none",
    "low",
    "medium",
    "high",
];
const XAI_45 = [
    "low",
    "medium",
    "high",
];
/*
 * Am 23.08.2026 an der Grok CLI 1.0.5 durchprobiert: `grok -m grok-4.6
 * --reasoning-effort xhigh` laeuft, dasselbe mit `grok-4.5` antwortet
 * „unknown effort level 'xhigh'; use one of: high, medium, low". Die CLI
 * prueft je Modell — `XAI_45` daneben stimmt also weiterhin.
 *
 * `none` und `max` kennt die Leiter von Grok gar nicht.
 */
const XAI_46 = [
    "low",
    "medium",
    "high",
    "xhigh",
];
const ANTHROPIC_4 = [
    "none",
    "low",
    "medium",
    "high",
    "max",
];
/**
 * Bewusste Allowlist aus den Anbieter-Verträgen. Nur Modellkennungen mit einer
 * dokumentierten Stufenmenge stehen hier. Datierte OpenAI-Snapshots erben die
 * Fähigkeiten ihres Alias; Pro- und unbekannte Varianten ausdrücklich nicht.
 */
export const KI_MODELL_CAPABILITY_REGISTRY = [
    {
        anbieterArt: "deepseek_api",
        modellMuster: /^deepseek-v4-pro$/,
        staerke: 4_200,
        reasoningStufen: DEEPSEEK_V4_PRO,
    },
    {
        anbieterArt: "deepseek_api",
        modellMuster: /^deepseek-v4-flash$/,
        staerke: 4_100,
        reasoningStufen: DEEPSEEK_V4_FLASH,
    },
    {
        anbieterArt: "openai_api",
        modellMuster: /^gpt-5\.6(?:-sol)?$/,
        staerke: 5_630,
        reasoningStufen: OPENAI_56,
    },
    {
        anbieterArt: "openai_api",
        modellMuster: /^gpt-5\.6-terra$/,
        staerke: 5_620,
        reasoningStufen: OPENAI_56,
    },
    {
        anbieterArt: "openai_api",
        modellMuster: /^gpt-5\.6-luna$/,
        staerke: 5_610,
        reasoningStufen: OPENAI_56,
    },
    {
        anbieterArt: "openai_api",
        modellMuster: /^gpt-5\.5(?:-2026-04-23)?$/,
        staerke: 5_500,
        reasoningStufen: OPENAI_54_55,
    },
    {
        anbieterArt: "openai_api",
        modellMuster: /^gpt-5\.4(?:-2026-03-05)?$/,
        staerke: 5_430,
        reasoningStufen: OPENAI_54_55,
    },
    {
        anbieterArt: "openai_api",
        modellMuster: /^gpt-5\.4-mini(?:-2026-03-17)?$/,
        staerke: 5_420,
        reasoningStufen: OPENAI_54_55,
    },
    {
        anbieterArt: "openai_api",
        modellMuster: /^gpt-5\.4-nano(?:-2026-03-17)?$/,
        staerke: 5_410,
        reasoningStufen: OPENAI_54_55,
    },
    {
        anbieterArt: "xai_api",
        modellMuster: /^grok-4\.6$/,
        staerke: 4_600,
        reasoningStufen: XAI_46,
    },
    {
        anbieterArt: "xai_api",
        modellMuster: /^grok-4\.5$/,
        staerke: 4_500,
        reasoningStufen: XAI_45,
    },
    {
        anbieterArt: "xai_api",
        modellMuster: /^grok-4\.3$/,
        staerke: 4_300,
        reasoningStufen: XAI_43,
    },
    {
        anbieterArt: "anthropic_subscription",
        modellMuster: /^(opus|claude-opus-4(?:[.-]\d+)?)$/,
        staerke: 4_800,
        reasoningStufen: ANTHROPIC_4,
    },
    {
        anbieterArt: "anthropic_subscription",
        modellMuster: /^(sonnet|claude-sonnet-4(?:[.-]\d+)?)$/,
        staerke: 4_600,
        reasoningStufen: ANTHROPIC_4,
    },
    {
        anbieterArt: "anthropic_subscription",
        modellMuster: /^(haiku|claude-haiku-4(?:[.-]\d+)?)$/,
        staerke: 4_200,
        reasoningStufen: ANTHROPIC_4,
    },
];
/**
 * Der Zugangsweg ändert nicht, was ein Modell kann.
 *
 * Ob `gpt-5.6` über einen Projekt-API-Schlüssel oder über die mit einem
 * Abonnement angemeldete Codex CLI erreicht wird, ist eine Frage der
 * Abrechnung — dieselbe Kennung meint dasselbe Modell. Die Registry führt
 * OpenAI-Modelle deshalb genau einmal, unter `openai_api`, und Grok-Modelle
 * genau einmal unter `xai_api`.
 */
function registryArt(anbieterArt) {
    if (anbieterArt === "openai_subscription")
        return "openai_api";
    if (anbieterArt === "xai_subscription")
        return "xai_api";
    return anbieterArt;
}
/** Ermittelt ausschließlich explizit bekannte Fähigkeiten eines Modells. */
export function ermittleKiModellCapability(anbieterArt, modellRoh) {
    const modell = modellRoh.trim();
    const gesucht = registryArt(anbieterArt);
    const regel = KI_MODELL_CAPABILITY_REGISTRY.find((kandidat) => kandidat.anbieterArt === gesucht &&
        kandidat.modellMuster.test(modell));
    return regel
        ? {
            modell,
            staerke: regel.staerke,
            reasoningStufen: regel.reasoningStufen,
        }
        : { modell, staerke: 0, reasoningStufen: NONE };
}
function vergleicheModellkennung(a, b) {
    if (a === b)
        return 0;
    return a < b ? -1 : 1;
}
/**
 * Liefert eine von Eingabereihenfolge und doppelten API-Einträgen unabhängige
 * Liste: zuerst stärkere, bei gleicher Stärke lexikografisch kleinere Modelle.
 */
export function listeKiModellCapabilities(anbieterArt, modelle) {
    const eindeutig = [...new Set(modelle.map((modell) => modell.trim()).filter(Boolean))];
    return eindeutig
        .map((modell) => ermittleKiModellCapability(anbieterArt, modell))
        .sort((a, b) => b.staerke - a.staerke || vergleicheModellkennung(a.modell, b.modell));
}
/** Höchste tatsächlich angebotene Stufe; leere Listen fallen sicher auf none. */
export function hoechsteReasoningStufe(stufen) {
    for (let index = REASONING_STUFEN.length - 1; index >= 0; index -= 1) {
        const kandidat = REASONING_STUFEN[index];
        if (stufen.includes(kandidat))
            return kandidat;
    }
    return "none";
}
/** Standard für einen Lauf: stärkstes bekanntes Modell und dessen höchste Stufe. */
export function waehleKiModellStandard(anbieterArt, modelle) {
    const capability = listeKiModellCapabilities(anbieterArt, modelle)[0];
    if (!capability)
        return null;
    return {
        ...capability,
        reasoningStufe: hoechsteReasoningStufe(capability.reasoningStufen),
    };
}
//# sourceMappingURL=ki-modelle.js.map